import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { checkSignupEmail, notifyAdmin, submitStoreAccessRequest } from '../supabaseClient';
import { INDUSTRY_OPTIONS } from '../constants/industryOptions';
import './StoreRequestForm.css';

const EMPTY_FORM = {
  firstName: '',
  lastName: '',
  dateOfBirth: '',
  email: '',
  phone: '',
  storeName: '',
  industry: '',
};

const REQUIRED_FIELD_LABELS = {
  firstName: 'First Name',
  lastName: 'Last Name',
  dateOfBirth: 'Date of Birth',
  email: 'Email Address',
  phone: 'Phone Number',
  storeName: 'Store Name',
  industry: 'Industry',
};

const getAge = (dateOfBirth) => {
  if (!dateOfBirth) return null;

  const birthDate = new Date(dateOfBirth);
  if (Number.isNaN(birthDate.getTime())) return null;

  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDifference = today.getMonth() - birthDate.getMonth();

  if (monthDifference < 0 || (monthDifference === 0 && today.getDate() < birthDate.getDate())) {
    age -= 1;
  }

  return age;
};

const StoreRequestForm = ({
  title = 'Request your first store',
  subtitle = 'Tell Servsolo about your business and we will review your first store request.',
  submitLabel = 'Request Store Access',
  user = null,
  compact = false,
}) => {
  const [form, setForm] = useState(() => ({
    ...EMPTY_FORM,
    email: user?.email || '',
  }));
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [existingAccount, setExistingAccount] = useState(false);
  const [existingPendingRequest, setExistingPendingRequest] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    if (user?.email) {
      setForm((current) => ({ ...current, email: user.email }));
    }
  }, [user?.email]);

  const normalizedEmail = useMemo(() => form.email.trim().toLowerCase(), [form.email]);

  const handleFieldChange = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setFieldErrors((current) => {
      if (!current[field]) return current;

      const next = { ...current };
      delete next[field];
      return next;
    });
    setError('');
    setSuccess('');
  };

  const validateForm = () => {
    const nextErrors = {};

    if (!form.firstName.trim()) nextErrors.firstName = 'First Name is required.';
    if (!form.lastName.trim()) nextErrors.lastName = 'Last Name is required.';

    if (!form.dateOfBirth) {
      nextErrors.dateOfBirth = 'Date of Birth is required.';
    } else {
      const age = getAge(form.dateOfBirth);
      if (age === null) {
        nextErrors.dateOfBirth = 'Enter a valid Date of Birth.';
      } else if (age < 18) {
        nextErrors.dateOfBirth = 'You must be 18 years old or older.';
      }
    }

    if (!normalizedEmail) {
      nextErrors.email = 'Email Address is required.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) {
      nextErrors.email = 'Enter a valid Email Address.';
    }

    if (!form.phone.trim()) nextErrors.phone = 'Phone Number is required.';
    if (!form.storeName.trim()) nextErrors.storeName = 'Store Name is required.';
    if (!form.industry) nextErrors.industry = 'Industry is required.';

    return nextErrors;
  };

  const handleEmailCheck = async () => {
    if (!normalizedEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) return;

    setCheckingEmail(true);
    try {
      const result = await checkSignupEmail(normalizedEmail);
      setExistingAccount(Boolean(result.accountExists));
      setExistingPendingRequest(Boolean(result.pendingRequestExists));
    } catch {
      // Do not block form entry if the hint lookup fails.
    } finally {
      setCheckingEmail(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');
    setExistingAccount(false);
    setExistingPendingRequest(false);

    const validationErrors = validateForm();
    setFieldErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      const missingFields = Object.keys(validationErrors)
        .filter((field) => validationErrors[field].includes('required'))
        .map((field) => REQUIRED_FIELD_LABELS[field]);

      if (missingFields.length > 0) {
        setError(`Please complete the required fields: ${missingFields.join(', ')}.`);
      } else if (validationErrors.dateOfBirth) {
        setError(validationErrors.dateOfBirth);
      } else {
        setError('Please correct the highlighted fields before sending your request.');
      }
      return;
    }

    setSubmitting(true);
    try {
      if (!user) {
        try {
          const status = await checkSignupEmail(normalizedEmail);
          setExistingAccount(Boolean(status.accountExists));
          setExistingPendingRequest(Boolean(status.pendingRequestExists));

          if (status.accountExists) {
            setError('You already have an account with this email. Please log in instead of creating another signup request.');
            return;
          }

          if (status.pendingRequestExists) {
            setError('There is already a pending store request for this email. Please wait for Servsolo approval or contact support.');
            return;
          }
        } catch {
          // Fall back to database validation if the public pre-check is unavailable.
        }
      }

      await submitStoreAccessRequest({
        requested_by_user_id: user?.id || null,
        first_name: form.firstName.trim(),
        last_name: form.lastName.trim(),
        date_of_birth: form.dateOfBirth,
        email: normalizedEmail,
        phone: form.phone.trim(),
        store_name: form.storeName.trim(),
        industry: form.industry,
      });

      notifyAdmin({
        kind: 'store-request',
        email: normalizedEmail,
        name: `${form.firstName.trim()} ${form.lastName.trim()}`,
        subject: `New store request: ${form.storeName.trim()}`,
        message: `Industry: ${form.industry}\nPhone: ${form.phone.trim()}\nRequested from: ${user ? 'signed-in access-pending flow' : 'public signup flow'}`,
        metadata: {
          storeName: form.storeName.trim(),
          industry: form.industry,
          source: user ? 'access-pending' : 'signup',
        },
      }).catch(() => {});

      setSuccess('Store request sent. Servsolo admin can now review and approve your first store.');
    } catch (requestError) {
      const message = requestError.message || 'Failed to send store request.';

      if (message.toLowerCase().includes('store_access_requests_pending_email_uidx') || message.toLowerCase().includes('duplicate key')) {
        setExistingPendingRequest(true);
        setError('There is already a pending store request for this email. Please wait for Servsolo approval or contact support.');
      } else {
        setError(message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={`store-request-shell${compact ? ' compact' : ''}`}>
      <div className="store-request-header">
        <h2>{title}</h2>
        <p>{subtitle}</p>
      </div>

      <form className="store-request-form" onSubmit={handleSubmit}>
        <div className="store-request-grid two-col">
          <label>
            <span>First Name <strong className="store-request-required">*</strong></span>
            <input className={fieldErrors.firstName ? 'store-request-input-error' : ''} value={form.firstName} onChange={(e) => handleFieldChange('firstName', e.target.value)} placeholder="Jane" />
            {fieldErrors.firstName && <small className="store-request-field-error">{fieldErrors.firstName}</small>}
          </label>
          <label>
            <span>Last Name <strong className="store-request-required">*</strong></span>
            <input className={fieldErrors.lastName ? 'store-request-input-error' : ''} value={form.lastName} onChange={(e) => handleFieldChange('lastName', e.target.value)} placeholder="Smith" />
            {fieldErrors.lastName && <small className="store-request-field-error">{fieldErrors.lastName}</small>}
          </label>
        </div>

        <div className="store-request-grid two-col">
          <label>
            <span>Date of Birth <strong className="store-request-required">*</strong></span>
            <input className={fieldErrors.dateOfBirth ? 'store-request-input-error' : ''} type="date" value={form.dateOfBirth} onChange={(e) => handleFieldChange('dateOfBirth', e.target.value)} />
            {fieldErrors.dateOfBirth && <small className="store-request-field-error">{fieldErrors.dateOfBirth}</small>}
          </label>
          <label>
            <span>Phone Number <strong className="store-request-required">*</strong></span>
            <input className={fieldErrors.phone ? 'store-request-input-error' : ''} value={form.phone} onChange={(e) => handleFieldChange('phone', e.target.value)} placeholder="+61 400 000 000" />
            {fieldErrors.phone && <small className="store-request-field-error">{fieldErrors.phone}</small>}
          </label>
        </div>

        <label>
          <span>Email Address <strong className="store-request-required">*</strong></span>
          <input
            className={fieldErrors.email ? 'store-request-input-error' : ''}
            type="email"
            value={form.email}
            onChange={(e) => handleFieldChange('email', e.target.value)}
            onBlur={handleEmailCheck}
            placeholder="owner@yourstore.com"
            disabled={Boolean(user?.email)}
          />
          {fieldErrors.email && <small className="store-request-field-error">{fieldErrors.email}</small>}
          <small>{checkingEmail ? 'Checking account status...' : 'Use the email you plan to access Servsolo with.'}</small>
        </label>

        {existingAccount && !user && (
          <div className="store-request-inline-note warning">
            You already have an account with this email. <Link to="/login">Please log in</Link> instead of starting a new signup.
          </div>
        )}

        {existingPendingRequest && (
          <div className="store-request-inline-note warning">
            A store request is already pending for this email. You can log in later or contact Servsolo for an update.
          </div>
        )}

        <div className="store-request-grid two-col">
          <label>
            <span>Store Name <strong className="store-request-required">*</strong></span>
            <input className={fieldErrors.storeName ? 'store-request-input-error' : ''} value={form.storeName} onChange={(e) => handleFieldChange('storeName', e.target.value)} placeholder="e.g. Ali's Kebab House" />
            {fieldErrors.storeName && <small className="store-request-field-error">{fieldErrors.storeName}</small>}
          </label>
          <label>
            <span>Industry <strong className="store-request-required">*</strong></span>
            <select className={fieldErrors.industry ? 'store-request-input-error' : ''} value={form.industry} onChange={(e) => handleFieldChange('industry', e.target.value)}>
              <option value="">Select industry</option>
              {INDUSTRY_OPTIONS.map((industry) => (
                <option key={industry} value={industry}>{industry}</option>
              ))}
            </select>
            {fieldErrors.industry && <small className="store-request-field-error">{fieldErrors.industry}</small>}
          </label>
        </div>

        {error && <div className="store-request-feedback error">{error}</div>}
        {success && <div className="store-request-feedback success">{success}</div>}

        <div className="store-request-actions">
          <button type="submit" disabled={submitting || existingPendingRequest || (existingAccount && !user)}>
            {submitting ? 'Sending...' : submitLabel}
          </button>
        </div>
      </form>
    </div>
  );
};

export default StoreRequestForm;